import React from "react";
import DutchAddressFields from "../../../components/DutchAddressFields";

export const NEW_PARTNER_REASON = "NEW_PARTNER";

export const emptyNewPartner = () => ({
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  sameAddress: true,
  address: "",
  zipCode: "",
  city: "",
  familySituation: "",
});

/** Situations in which the family is still registered as living together with the other parent. */
const STILL_TOGETHER = ["MARRIED", "COHABITING"];

/** The parent has to confirm or change the situation of the current family before reporting a new partner. */
export const needsSituationChoice = (currentSituation) => STILL_TOGETHER.includes(currentSituation || "MARRIED");

/** Complete enough to send: name and a valid-looking e-mail address (the partner gets an account). */
export const isNewPartnerComplete = (partner) =>
  Boolean(partner?.firstName?.trim() && partner?.lastName?.trim() && /\S+@\S+\.\S+/.test(partner?.email?.trim() || ""));

/**
 * Mutation for "new partner": processed from the school's mutations (manual or automatic, per the mutation settings).
 * With the same address the address of the reporting parent is used.
 */
export const buildNewPartnerMutationRequest = (familyId, partner, note, parentData, currentSituation) => {
  const changedSituation =
    partner?.familySituation && partner.familySituation !== (currentSituation || "MARRIED") ? partner.familySituation : null;
  const sameAddress = partner?.sameAddress !== false;
  const address = sameAddress
    ? { address: parentData?.address || "", zipCode: parentData?.zipCode || "", city: parentData?.city || "" }
    : { address: partner?.address || "", zipCode: partner?.zipCode || "", city: partner?.city || "" };
  return {
    entityType: "PARENT",
    action: "CREATE",
    entityId: null,
    payload: {
      newPartner: true,
      familyId,
      firstName: partner.firstName.trim(),
      lastName: partner.lastName.trim(),
      email: partner.email.trim(),
      phone: partner.phone?.trim() || "",
      sameAddress,
      ...address,
      note: note?.trim() || "",
      ...(changedSituation ? { familySituation: changedSituation, previousFamilySituation: currentSituation || "MARRIED" } : {}),
    },
  };
};

const defaultInput =
  "min-h-[46px] w-full rounded-2xl border border-slate-200 bg-white px-4 text-base font-medium text-slate-950 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 dark:border-slate-700 dark:bg-slate-950/60 dark:text-slate-100 dark:focus:ring-emerald-900/30";
const defaultLabel = "flex min-w-0 flex-col gap-1.5 text-sm font-bold text-slate-700 dark:text-slate-200";

/** The details of the new partner, asked right away when a parent reports a new partner. */
export default function NewPartnerFields({
  t,
  value,
  onChange,
  currentSituation,
  situationOptions = [],
  inputClassName = defaultInput,
  labelClassName = defaultLabel,
}) {
  const partner = value || emptyNewPartner();
  const set = (field, next) => onChange({ ...partner, [field]: next });
  const current = currentSituation || "MARRIED";
  const currentLabel = situationOptions.find((option) => option.value === current)?.label || current;

  return (
    <fieldset className="grid gap-4 sm:grid-cols-2">
      {needsSituationChoice(current) && (
        <div className="grid gap-3 rounded-2xl bg-amber-50 px-4 py-3 ring-1 ring-amber-200 dark:bg-amber-950/30 dark:ring-amber-900/50 sm:col-span-2">
          <p className="m-0 text-sm font-bold text-amber-900 dark:text-amber-100">
            {t("parentPortal.newPartner.situationNotice", {
              defaultValue: "Your family is still registered as \"{{situation}}\". Should the situation of your current family change, for example to divorced or other?",
              situation: currentLabel,
            })}
          </p>
          <label className={labelClassName}>
            {t("parentPortal.newPartner.situationLabel", { defaultValue: "Situation of your current family" })}
            <select
              required
              value={partner.familySituation || ""}
              onChange={(e) => set("familySituation", e.target.value)}
              className={inputClassName}
            >
              <option value="" disabled>
                {t("parentPortal.newPartner.situationChoose", { defaultValue: "Choose…" })}
              </option>
              {situationOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.value === current
                    ? t("parentPortal.newPartner.situationKeep", { defaultValue: "Stays {{situation}}", situation: option.label })
                    : option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
      <legend className="mb-2 text-sm font-black text-slate-950 dark:text-white sm:col-span-2">
        {t("parentPortal.newPartner.title", { defaultValue: "Details of your new partner" })}
      </legend>
      <label className={labelClassName}>
        {t("parentPortal.newPartner.firstName", { defaultValue: "First name" })}
        <input required value={partner.firstName} onChange={(e) => set("firstName", e.target.value)} className={inputClassName} autoComplete="off" />
      </label>
      <label className={labelClassName}>
        {t("parentPortal.newPartner.lastName", { defaultValue: "Last name" })}
        <input required value={partner.lastName} onChange={(e) => set("lastName", e.target.value)} className={inputClassName} autoComplete="off" />
      </label>
      <label className={labelClassName}>
        {t("parentPortal.newPartner.email", { defaultValue: "E-mail address" })}
        <input required type="email" value={partner.email} onChange={(e) => set("email", e.target.value)} className={inputClassName} autoComplete="off" />
      </label>
      <label className={labelClassName}>
        {t("parentPortal.newPartner.phone", { defaultValue: "Phone number" })}
        <input type="tel" value={partner.phone} onChange={(e) => set("phone", e.target.value)} className={inputClassName} autoComplete="off" />
      </label>
      <label className="flex items-center gap-3 text-sm font-bold text-slate-700 dark:text-slate-200 sm:col-span-2">
        <input type="checkbox" checked={partner.sameAddress !== false} onChange={(e) => set("sameAddress", e.target.checked)} className="h-5 w-5 rounded border-slate-300 text-emerald-600" />
        {t("parentPortal.newPartner.sameAddress", { defaultValue: "My partner lives at my address" })}
      </label>
      {partner.sameAddress === false && (
        <DutchAddressFields
          address={partner.address}
          zipCode={partner.zipCode}
          city={partner.city}
          onChange={(field, next) => set(field, next)}
          onAddressFound={(found) => onChange({ ...partner, ...found })}
          t={t}
          inputClassName={inputClassName}
          labelClassName={labelClassName}
          addressClassName="sm:col-span-2"
        />
      )}
      <p className="m-0 rounded-2xl bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900 ring-1 ring-amber-100 dark:bg-amber-950/30 dark:text-amber-100 dark:ring-amber-900/50 sm:col-span-2">
        {t("parentPortal.newPartner.accountNotice", {
          defaultValue:
            "After the school processes this, your partner gets their own account for the parent portal and can see the details of the children.",
        })}
      </p>
    </fieldset>
  );
}
